import React, { ChangeEvent } from "react";
import PageGroup, { fixedPageGroupTags } from "../models/PageGroup";
import Page from "../models/Page";
import PageGroupDialog from "../modelDialogs/PageGroupDialog";
import TreeViewModel from "../models/TreeviewModel";
import TreeNodeInfo from "../models/TreeNodeInfo";
import PageReference from "../models/PageReference";

import Logger from "../Logger";
import { showToast } from "../components/Toasts";
import Room from "../models/Room";
import RoomTreeViewModel from "./RoomTreeviewModel";
import { itx } from "../translationItx/itx";
import { pageTypeInfo, PageTypes } from "../models/PageTypes";
import { ProjectLicenseType } from "../models/ProjectLicense";
import ToShopLicensesDialog from "../modelDialogs/ToShopLicensesDialog";

const logger = new Logger();

export default class PageGroupTreeViewModel extends TreeViewModel {
  get typedObj(): PageGroup {
    return this.obj as PageGroup;
  }

  get isFolder(): boolean {
    const pageType = this.typedObj.pages.asArray()[0]?.documentType?.type;
    if (pageType && pageTypeInfo(pageType)?.isList) {
      return false;
    }
    return true;
  }

  getTagNeeded(): boolean {
    return false;
  }
  getDisableEdit(): boolean {
    return true;
  }

  getChildCollectionEx(parentTreeNodeInfo: TreeNodeInfo): Array<TreeNodeInfo> | undefined {
    const children = this.typedObj.sortedPages;

    if (children) {
      const childrenEx = new Array<TreeNodeInfo>();

      children.forEach((child) => {
        if (child.className == "PageReference") {
          // check here if page is from isFloorGeneral
          const room = (child as PageReference).referencedPage?.parent as Room;
          if (!(room.className == "Room" && (room.treeViewModel as RoomTreeViewModel)?.isFloorGeneral())) {
            childrenEx.push(new TreeNodeInfo(parentTreeNodeInfo, child, (child as PageReference).referencedPage));
          }
        } else {
          const page = child as Page;
          let showChild = true;
          if (!state.settings.showCabinetPlaning) {
            // dont show following lists if NOT in cabinet Planing
            const dontShowList = [
              PageTypes.fuselist,
              PageTypes.actorinstalationslist,
              PageTypes.terminallist,
              // PageTypes.cabletypelist,
            ];

            if (dontShowList.includes(page.documentType.type)) {
              showChild = false;
            }
          }
          if (showChild) {
            childrenEx.push(new TreeNodeInfo(parentTreeNodeInfo, child));
          }
        }
      });

      /////////////////////////////////////////////////////

      const pageGroups = this.typedObj.pageGroups.sortIntBy("tag");
      pageGroups.forEach((child) => {
        // for old projects
        let newPage = new Page({}, this.typedObj);

        const tag = state.translate(
          `pageDocumentType.${newPage.documentType.type}Tag`,
          newPage.documentType.type.substring(0, 2).toUpperCase(),
        );
        let name = state.translate(`pageDocumentType.${newPage.documentType.type}`, newPage.documentType.type);
        newPage.documentType.name = name;
        newPage.tag = tag;
        newPage.name = name;
        if (child.className == "PageReference") {
          childrenEx.push(new TreeNodeInfo(parentTreeNodeInfo, child));
        } else {
          childrenEx.push(new TreeNodeInfo(parentTreeNodeInfo, newPage));
        }

        // consistency check
        child.consistencyCheck(this.typedObj);
      });

      return childrenEx;
    }

    return undefined;
  }

  ////////////////////////////////////////////////////////////////////////////////////////
  //
  // inline menu buttons
  //, super.inlineButton_Add("pageGroupTreeViewModel.selectAndAddPdf")
  ////////////////////////////////////////////////////////////////////////////////////////

  handleOpen(transitOpen: boolean = false): boolean {
    const id = this.typedObj.pages.asArray()[0]?.id;
    const pageType: PageTypes | undefined = this.typedObj.pages.asArray()[0]?.documentType?.type;

    if (id && pageType && pageTypeInfo(pageType)?.isList) {
      // check here first if list is licensed
      if (state.projectLicense.checkLicenses(ProjectLicenseType.Documentation)) {
        return this.handleOpenId(id, transitOpen);
      }

      state.dialogManager.openDialog(<ToShopLicensesDialog />);
      return false;
    } else {
      return false;
    }
  }

  getButtons(): JSX.Element[] | undefined {
    if (this.typedObj.tag == fixedPageGroupTags.buildingPlans) {
      return [this.inlineButton_Add()]; //inlineButton_Add-> choose which lists(floorRoomList, sceneList,actorList etc. should be inside of documents)
    } else {
      const pageType: PageTypes | undefined = this.typedObj.pages.asArray()[0]?.documentType?.type;
      if (pageTypeInfo(pageType)?.isList) {
        return [this.inlineButton_Open("inlineButton.openDocument")]; // no itx
      } else {
        return [];
      }
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////
  //
  // context menu
  //
  ////////////////////////////////////////////////////////////////////////////////////////

  // Gibt die neu angelegte Page (das PDF-Dokument) zurück, sobald sie fertig eingelesen und
  // hinzugefügt wurde - wird für die KI-Erkennung benötigt, um später die passende PDF-Seite
  // als Hintergrundbild einer Etage zu setzen (siehe LoadFloorPlans.tsx).
  createPageFromPdfFile(e: ChangeEvent<HTMLInputElement>): Promise<Page | undefined> {
    const self = this;
    return new Promise((resolve) => {
      if (e.target.files && e.target.files[0]) {
        const file = e.target.files[0];

        if (file.type == "application/pdf") {
          const reader = new FileReader();
          reader.readAsBinaryString(file);
          reader.onloadend = function () {
            var data = reader.result;
            const nameWithoutExtension = file.name.substring(0, file.name.length - 4);
            const page = new Page({ name: nameWithoutExtension }, self.typedObj);
            page.documentType.type = PageTypes.pdf;
            page.documentType.name = PageTypes.pdf;
            var tag = `${self.typedObj.pages.size + 1}`;
            if (tag.length < 2) {
              tag = "0" + tag;
            }

            page.tag = tag;
            page.name = nameWithoutExtension;

            self.typedObj.pages.add(page);
            const pdfFile = page.pdfFile;
            pdfFile.pdfData = data;
            page.markAsDirty();
            self.typedObj.raiseOnChange("");
            self.typedObj.raiseOnChange("childCollection");
            self.typedObj.raiseOnChange("expanded");
            resolve(page);
          };
        } else {
          showToast({ text: itx("pageGroupTreeViewModel.onlyPdfSupported", "Es wird nur PDF´s unterstützt") });
          resolve(undefined);
        }
      } else {
        resolve(undefined);
      }
    });
  }
  handleAdd() {
    if (this.typedObj.tag == fixedPageGroupTags.buildingPlans) {
      const fileInput: HTMLInputElement = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = ".pdf";

      //
      // not working, not existing, not possible: fileInput.title = "Gebäudedokumentation auswählen";
      //
      fileInput.onchange = (event: Event) => {
        this.createPageFromPdfFile(event as unknown as ChangeEvent<HTMLInputElement>);
      };
      fileInput.click();
    }
  }

  inlineButton_Add() {
    return super.inlineButton_Add("pageGroupTreeViewModel.selectAndAddPdf"); // no itx
  }

  handleProperties() {
    state.dialogManager.openDialog(<PageGroupDialog obj={this.typedObj} />);
  }

  ////////////////////////////////////////////////////////////////////////////////////////

  async getDisabledContextMenuItems(): Promise<Array<string>> {
    const copiedObjClassName = state.clipboard.getClassName();

    if (
      (copiedObjClassName && copiedObjClassName == "Room") ||
      (copiedObjClassName && copiedObjClassName == "Floor") ||
      (copiedObjClassName && copiedObjClassName == "Consumer") ||
      (copiedObjClassName && copiedObjClassName == "Button") ||
      (copiedObjClassName && copiedObjClassName == "Actor")
    ) {
      return new Array<string>();
    }

    return ["paste"]; // no itx
  }

  get contextMenuItems(): any {
    const pageGroup = this.obj as PageGroup;
    var subLabelPaste = undefined;
    let subLableAdd = "ContextMenuLables.addPageGroup"; // no itx
    try {
      subLabelPaste = state.contextMenuManager.clipboardData.items[0].name;
    } catch (ex) {
      logger.error(ex);
    }
    if ((pageGroup.tag = "01")) {
      subLableAdd = "ContextMenuLables.addNewList"; // no itx
    } else if (pageGroup.tag == "03") {
      subLableAdd = "ContextMenuLables.addNewBuildingPlans"; // no itx
    }
    if (this.typedObj.tag == fixedPageGroupTags.buildingPlans) {
      return {
        title: this.obj?.name,
        items: [
          // this.baseContextMenuItems_Paste(subLabelPaste),
          this.baseContextMenuItems_Add(subLableAdd),
          //this.baseContextMenuItems_Properties(),
        ],
      };
    } else {
      return {
        title: this.obj?.name,
        items: [
          //this.baseContextMenuItems_Paste(subLabelPaste),
          //this.baseContextMenuItems_Properties(),
        ],
      };
    }
  }
}
